/**
 * React Query hooks for fetching power distribution configurations
 * @see GitLab #996 — Repeating Sections (Facility Power / Power Distribution)
 */

import { useQuery } from '@tanstack/react-query';

import { isRepeatingCollectionRouteNotFound } from './repeatingCollectionRouteNotFound';
import { useNetworkStatus } from './useOfflineRequestsQuery';
import { groupPowerDistributionByStatus } from '../../components/powerdistribution/powerDistributionListUtils';
import { api } from '../../services/api';
import {
  applyPendingPowerDistributionOperations,
  getCachedPowerDistributionList,
  getOfflinePowerDistributionById,
  getOfflinePowerDistributionGrouped,
  getOfflinePowerDistributionList,
  getInFlightPowerDistributionOpsForRequest,
  mirrorPowerDistributionToOfflineRequestCache,
} from '../../services/powerDistributionOfflineSync';

import type {
  PowerDistributionConfiguration,
  PowerDistributionConfigurationsResponse,
  GroupedPowerDistributionConfigurations,
} from '../../types/powerDistribution';

/**
 * Query keys for consistent cache management
 */
export const powerDistributionConfigurationsKeys = {
  all: ['powerDistributionConfigurations'] as const,
  lists: () => [...powerDistributionConfigurationsKeys.all, 'list'] as const,
  list: (requestId: string) =>
    [...powerDistributionConfigurationsKeys.lists(), requestId] as const,
  grouped: (requestId: string) =>
    [...powerDistributionConfigurationsKeys.all, 'grouped', requestId] as const,
  details: () =>
    [...powerDistributionConfigurationsKeys.all, 'detail'] as const,
  detail: (requestId: string, powerDistributionId: number) =>
    [
      ...powerDistributionConfigurationsKeys.details(),
      requestId,
      powerDistributionId,
    ] as const,
};

/**
 * Hook for fetching all power distribution configurations for a request
 * Includes offline fallback to return cached configurations
 */
export const usePowerDistributionConfigurations = (
  requestId: string,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: powerDistributionConfigurationsKeys.list(requestId),
    queryFn: async () => {
      if (!isOnline) {
        return getOfflinePowerDistributionList(requestId);
      }

      try {
        const response = await api.get<PowerDistributionConfigurationsResponse>(
          `/requests/${requestId}/power-distribution`,
        );
        await mirrorPowerDistributionToOfflineRequestCache(
          requestId,
          response.powerDistribution,
        );
        const pendingOps = await getInFlightPowerDistributionOpsForRequest(
          requestId,
        );
        return applyPendingPowerDistributionOperations(
          requestId,
          response.powerDistribution,
          pendingOps,
        );
      } catch (error) {
        if (isRepeatingCollectionRouteNotFound(error)) {
          return [];
        }
        throw error;
      }
    },
    enabled: !!requestId && enabled,
    staleTime: 30 * 1000, // 30 seconds
    retry: (failureCount, error) =>
      isRepeatingCollectionRouteNotFound(error) ? false : failureCount < 3,
    meta: {
      errorMessage: 'Failed to fetch power distribution configurations.',
    },
  });
};

/**
 * Hook for fetching power distribution configurations grouped by status (existing vs new)
 * Includes offline fallback (derived from cached list)
 */
export const usePowerDistributionConfigurationsGrouped = (
  requestId: string,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: powerDistributionConfigurationsKeys.grouped(requestId),
    queryFn: async () => {
      if (!isOnline) {
        return getOfflinePowerDistributionGrouped(requestId);
      }

      const response = await api.get<GroupedPowerDistributionConfigurations>(
        `/requests/${requestId}/power-distribution/grouped`,
      );
      const allUnits = [...response.existing, ...response.new];
      await mirrorPowerDistributionToOfflineRequestCache(requestId, allUnits);
      const pendingOps = await getInFlightPowerDistributionOpsForRequest(
        requestId,
      );
      return groupPowerDistributionByStatus(
        applyPendingPowerDistributionOperations(
          requestId,
          allUnits,
          pendingOps,
        ),
      );
    },
    enabled: !!requestId && enabled,
    staleTime: 30 * 1000,
    meta: {
      errorMessage:
        'Failed to fetch grouped power distribution configurations.',
    },
  });
};

/**
 * Hook for fetching a single power distribution configuration
 * Includes offline fallback from cached request units
 */
export const usePowerDistributionConfiguration = (
  requestId: string,
  powerDistributionId: number,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: powerDistributionConfigurationsKeys.detail(
      requestId,
      powerDistributionId,
    ),
    queryFn: async () => {
      if (!isOnline) {
        const cached = await getOfflinePowerDistributionById(
          requestId,
          powerDistributionId,
        );
        if (!cached) {
          throw new Error(
            'Power distribution configuration not found in offline cache',
          );
        }
        return cached;
      }

      const response = await api.get<PowerDistributionConfiguration>(
        `/requests/${requestId}/power-distribution/${powerDistributionId}`,
      );
      const list = await getCachedPowerDistributionList(requestId);
      const merged = list.some(u => u.id === response.id)
        ? list.map(u => (u.id === response.id ? response : u))
        : [...list, response];
      await mirrorPowerDistributionToOfflineRequestCache(requestId, merged);
      return response;
    },
    enabled: !!requestId && !!powerDistributionId && enabled,
    staleTime: 30 * 1000,
    meta: {
      errorMessage: 'Failed to fetch power distribution configuration details.',
    },
  });
};
